import { create } from "zustand";
import { persist } from "zustand/middleware";
import { apiClient } from "../services/api";
import { loginLocalUser, registerLocalUser } from "./authFallback";

function parseAuthError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join(", ") || fallback;
  }
  if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return fallback;
}

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      loading: false,
      error: null,

      login: async (email, password) => {
        set({ loading: true, error: null });
        try {
          const { data } = await apiClient.post("/auth/login", { email, password });
          localStorage.setItem("ff_token", data.token);
          localStorage.removeItem("ff_refresh");
          set({ user: data.user, token: data.token, loading: false });
          return data.user;
        } catch (e) {
          const fallbackUser = loginLocalUser(email, password);
          if (fallbackUser) {
            localStorage.setItem("ff_token", `local-${fallbackUser.user_id}`);
            set({ user: fallbackUser, token: `local-${fallbackUser.user_id}`, loading: false });
            return fallbackUser;
          }
          const msg = parseAuthError(e, "Login failed");
          set({ loading: false, error: msg });
          throw new Error(msg);
        }
      },

      register: async (name, email, password, consent = {}) => {
        set({ loading: true, error: null });
        try {
          const { data } = await apiClient.post("/auth/register", {
            name, email, password,
            age_confirmed: !!consent.age_confirmed,
            terms_accepted: !!consent.terms_accepted,
            marketing_consent: !!consent.marketing_consent,
          });
          localStorage.setItem("ff_token", data.token);
          set({ user: data.user, token: data.token, loading: false });
          return data.user;
        } catch (e) {
          try {
            const user = registerLocalUser(name, email, password, consent);
            localStorage.setItem("ff_token", `local-${user.user_id}`);
            set({ user, token: `local-${user.user_id}`, loading: false });
            return user;
          } catch (registerErr) {
            const msg = parseAuthError(e, registerErr?.message || "Sign-up failed");
            set({ loading: false, error: msg });
            throw new Error(msg);
          }
        }
      },

      googleExchange: async (sessionId) => {
        set({ loading: true, error: null });
        try {
          const { data } = await apiClient.post("/auth/google/exchange", { session_id: sessionId });
          if (data.token) {
            localStorage.setItem("ff_token", data.token);
          }
          set({ user: data.user, token: data.token || null, loading: false });
          return data.user;
        } catch (e) {
          const msg = parseAuthError(e, "Google sign-in failed");
          set({ loading: false, error: msg });
          throw new Error(msg);
        }
      },

      checkAuth: async () => {
        try {
          const { data } = await apiClient.get("/auth/me");
          set({ user: data });
          return data;
        } catch {
          // Token/refresh both failed. Purge every trace so persist doesn't
          // rehydrate stale user next mount, and so the next protected route
          // sees a clean unauthenticated state instead of a "ghost" user
          // object attached to a dead token.
          localStorage.removeItem("ff_token");
          localStorage.removeItem("ff_refresh");
          set({ user: null, token: null });
          return null;
        }
      },

      saveProfile: async (profileData) => {
        const { data } = await apiClient.post("/auth/profile-setup", profileData);
        set({ user: data });
        return data;
      },

      setUser: (user) => set({ user }),

      logout: async () => {
        try {
          await apiClient.post("/auth/logout");
        } catch {}
        localStorage.removeItem("ff_token");
        localStorage.removeItem("ff_refresh");
        set({ user: null, token: null });
      },
    }),
    {
      name: "fitfrik-auth",
      partialize: (s) => ({ token: s.token, user: s.user }),
    }
  )
);
